import { IconTrash } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { DataList, type DataListColumn } from "@/components/ui/data-list"
import type { CalibrationPoint } from "@/types/device"

interface CalibrationTableProps {
  points: CalibrationPoint[]
  onRemove: (index: number) => void
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString()
}

function offsetClass(offset: number): string {
  return offset >= 0 ? "text-green-600" : "text-red-600"
}

function formatOffset(offset: number): string {
  return `${offset >= 0 ? "+" : ""}${offset.toFixed(4)}`
}

export function CalibrationTable({ points, onRemove }: CalibrationTableProps) {
  if (points.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No calibration points yet. Capture a point to begin calibration.
      </div>
    )
  }

  const columns: DataListColumn<CalibrationPoint>[] = [
    {
      key: "index",
      header: "#",
      cell: (_row, index) => <span className="font-medium">{index + 1}</span>,
      headClassName: "w-20",
    },
    {
      key: "raw_sg",
      header: "Raw SG",
      cell: (row) => <span className="tabular-nums">{row.raw_sg.toFixed(4)}</span>,
    },
    {
      key: "reference_sg",
      header: "Reference SG",
      cell: (row) => <span className="tabular-nums">{row.reference_sg.toFixed(4)}</span>,
    },
    {
      key: "offset",
      header: "Offset",
      cell: (row) => {
        const offset = row.reference_sg - row.raw_sg
        return (
          <span className={`tabular-nums ${offsetClass(offset)}`}>{formatOffset(offset)}</span>
        )
      },
    },
    {
      key: "temperature",
      header: "Temp",
      cell: (row) => <span className="tabular-nums">{row.temperature.toFixed(1)}°</span>,
    },
    {
      key: "captured_at",
      header: "Captured",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.captured_at)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (_row, index) => (
        <Button
          variant="ghost"
          size="icon-touch"
          onClick={() => onRemove(index)}
          className="text-muted-foreground hover:text-destructive"
        >
          <IconTrash className="size-4" />
        </Button>
      ),
      headClassName: "w-20",
    },
  ]

  return (
    <DataList
      rows={points}
      columns={columns}
      rowKey={(point, index) => `${point.captured_at}-${index}`}
      emptyState={null}
      mobileItem={(point, index) => {
        const offset = point.reference_sg - point.raw_sg
        return (
          <Card className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs text-muted-foreground">#{index + 1}</span>
                  <span className="text-lg font-medium tabular-nums">
                    {point.reference_sg.toFixed(4)}
                  </span>
                  <span className="text-xs text-muted-foreground">ref SG</span>
                </div>
                <div className="mt-1 flex items-baseline gap-3 text-sm tabular-nums text-muted-foreground">
                  <span>raw {point.raw_sg.toFixed(4)}</span>
                  <span className={offsetClass(offset)}>{formatOffset(offset)}</span>
                  <span>{point.temperature.toFixed(1)}°</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatDate(point.captured_at)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-touch"
                onClick={() => onRemove(index)}
                className="shrink-0 text-muted-foreground hover:text-destructive"
              >
                <IconTrash className="size-4" />
              </Button>
            </div>
          </Card>
        )
      }}
    />
  )
}
